import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

interface NoTeamsGateProps {
  /** Heading. Defaults to "No Teams Found"; the dashboard uses a welcome variant. */
  title?: string;
  /** Supporting line under the heading. */
  message?: string;
}

/**
 * Shown when the signed-in user has no teams yet.
 *
 * Single home for the "you need a team first" state, which was previously
 * copy-pasted across five pages. Routes to the /welcome persona picker
 * (sc-336) rather than straight to /team-setup, since a parent/player persona
 * never sees team setup at all. Includes a log-out link since a user stuck
 * here (e.g. mid-signup, before a team exists) otherwise has no way to sign
 * out of any of these five pages.
 */
export const NoTeamsGate: React.FC<NoTeamsGateProps> = ({
  title = 'No Teams Found',
  message = 'Get started by setting up your team.',
}) => {
  const { signOut } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={handleLogout}
          aria-label="Log out"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-700 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          <span>Log out</span>
        </button>
      </div>
      <div className="text-center">
        <h2 className="text-h3 mb-4">{title}</h2>
        <p className="mb-6 caption">{message}</p>
        <Link to="/welcome" className="btn-primary">
          Get Started
        </Link>
      </div>
    </div>
  );
};

export default NoTeamsGate;
